import { useEffect, useRef, useState } from "react";
import { Clapperboard, ImagePlus, Save, Trash2, Upload, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { photoSrc } from "@/components/Reviews";

const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";
const CHUNK = 4 * 1024 * 1024;
const randomId = () => [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");

async function uploadVideoChunked(file, onProgress) {
  const upload_id = randomId();
  const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", upload_id);
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), `part-${i}`);
    await api.post("/admin/upload-video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  const r = await api.post("/admin/upload-video/complete", { upload_id, total, filename: file.name });
  return r.data.url;
}

export default function AddonsAdmin() {
  const [addon, setAddon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(-1);
  const videoInput = useRef(null);
  const photoInput = useRef(null);

  useEffect(() => { api.get("/admin/addons").then((r) => setAddon(r.data[0])).catch(() => toast.error("Could not load add-ons")); }, []);
  if (!addon) return <p className="text-sm text-ink/50" data-testid="addons-loading">Loading add-ons…</p>;

  const set = (k, v) => setAddon((a) => ({ ...a, [k]: v }));
  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/admin/addons/drone", addon);
      setAddon(r.data);
      toast.success("Drone add-on saved — live on the site, brochures and rate cards");
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const uploadReel = async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) return toast.error("Video must be under 60 MB");
    setProgress(0);
    try {
      const url = await uploadVideoChunked(file, setProgress);
      const r = await api.put("/admin/addons/drone", { ...addon, reel_url: url });
      setAddon(r.data);
      toast.success("Sample reel uploaded and live");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setProgress(-1); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("photo", file);
    try {
      const r = await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...addon, images: [...addon.images, r.data.url] };
      const saved = await api.put("/admin/addons/drone", next);
      setAddon(saved.data);
      toast.success("Photo added");
    } catch { toast.error("Photo upload failed"); }
  };

  const removePhoto = async (i) => {
    const saved = await api.put("/admin/addons/drone", { ...addon, images: addon.images.filter((_, k) => k !== i) });
    setAddon(saved.data);
  };

  return (
    <div className="space-y-8" data-testid="addons-admin">
      <form onSubmit={save} className="bg-white border border-ink/10 rounded-3xl p-6 grid sm:grid-cols-2 gap-4" data-testid="addon-drone-form">
        <div className="sm:col-span-2 flex items-center justify-between gap-4">
          <h2 className="font-serif text-2xl font-bold text-ink flex items-center gap-2"><Clapperboard size={20} className="text-terra" /> Drone Aerial Shoot add-on</h2>
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="addon-enabled-label">
            <input type="checkbox" checked={!!addon.enabled} onChange={(e) => set("enabled", e.target.checked)} className="w-4 h-4 accent-terra" data-testid="addon-enabled-toggle" />
            {addon.enabled ? "Shown on site" : "Hidden"}
          </label>
        </div>
        <p className="sm:col-span-2 text-xs text-ink/60 -mt-2">Price, clip count and minutes update the website card, Package Builder, tour pages, brochures (EN/HI/OR) and rate cards instantly. Minutes = total footage across all clips.</p>
        {[["name", "Service name"], ["tagline", "Tagline"]].map(([k, label]) => (
          <div key={k}><label className="block text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-1.5">{label}</label>
            <input value={addon[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`addon-field-${k}`} /></div>
        ))}
        <div className="sm:col-span-2 grid grid-cols-3 gap-3">
          {[["price", "Price (₹)"], ["videos", "Number of clips"], ["minutes", "Total minutes"]].map(([k, label]) => (
            <div key={k}><label className="block text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-1.5">{label}</label>
              <input type="number" min="0" value={addon[k] ?? 0} onChange={(e) => set(k, Number(e.target.value))} className={inputCls} data-testid={`addon-field-${k}`} /></div>
          ))}
        </div>
        <div className="sm:col-span-2"><label className="block text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-1.5">Description</label>
          <textarea rows={3} value={addon.description || ""} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="addon-field-description" /></div>
        <div className="sm:col-span-2"><label className="block text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-1.5">Conditions</label>
          <input value={addon.conditions || ""} onChange={(e) => set("conditions", e.target.value)} className={inputCls} data-testid="addon-field-conditions" /></div>
        <div className="sm:col-span-2"><button type="submit" disabled={saving} data-testid="addon-save-button" className="inline-flex items-center gap-2 bg-ink text-sand text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-terra transition-colors disabled:opacity-60"><Save size={14} /> {saving ? "Saving…" : "Save add-on"}</button></div>
      </form>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-ink/10 rounded-3xl p-6" data-testid="addon-reel-panel">
          <h3 className="font-serif text-xl font-bold text-ink flex items-center gap-2"><Video size={18} className="text-terra" /> Sample reel</h3>
          <p className="text-xs text-ink/60 mt-1">MP4 up to 60 MB, ideally 15–30 s, landscape. Plays muted on loop inside the card; visitors can unmute or go fullscreen.</p>
          {addon.reel_url ? (
            <video src={photoSrc(addon.reel_url)} controls muted playsInline className="mt-4 w-full rounded-2xl bg-ink aspect-video" data-testid="addon-reel-preview" />
          ) : (
            <div className="mt-4 aspect-video rounded-2xl border-2 border-dashed border-ink/15 grid place-items-center text-xs text-ink/40" data-testid="addon-reel-empty">No reel uploaded yet — card shows photos only</div>
          )}
          <input ref={videoInput} type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={(e) => uploadReel(e.target.files?.[0])} data-testid="addon-reel-input" />
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <button type="button" onClick={() => videoInput.current?.click()} disabled={progress >= 0} data-testid="addon-reel-upload-button" className="inline-flex items-center gap-2 bg-terra text-sand text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-ink transition-colors disabled:opacity-60"><Upload size={14} /> {addon.reel_url ? "Replace reel" : "Upload reel"}</button>
            {addon.reel_url && <button type="button" onClick={async () => { const r = await api.put("/admin/addons/drone", { ...addon, reel_url: "" }); setAddon(r.data); toast.success("Reel removed"); }} data-testid="addon-reel-remove-button" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 border border-ink/15 px-4 py-2.5 rounded-full hover:text-terra"><Trash2 size={14} /> Remove</button>}
            {progress >= 0 && <span className="text-xs font-mono text-ink/60" data-testid="addon-reel-progress">Uploading… {progress}%</span>}
          </div>
          {progress >= 0 && <div className="mt-3 h-1.5 bg-sand rounded-full overflow-hidden"><div className="h-full bg-terra transition-[width]" style={{ width: `${progress}%` }} /></div>}
        </div>

        <div className="bg-white border border-ink/10 rounded-3xl p-6" data-testid="addon-photos-panel">
          <h3 className="font-serif text-xl font-bold text-ink flex items-center gap-2"><ImagePlus size={18} className="text-terra" /> Photos ({addon.images.length}/8)</h3>
          <p className="text-xs text-ink/60 mt-1">First photo is the card cover. JPG/PNG/WEBP under 6 MB.</p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {addon.images.map((src, i) => (
              <div key={src} className="relative aspect-[4/3] rounded-xl overflow-hidden border border-ink/10 group" data-testid={`addon-photo-${i + 1}`}>
                <img src={photoSrc(src)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} aria-label="Remove photo" data-testid={`addon-photo-remove-${i + 1}`} className="absolute top-1.5 right-1.5 bg-ink/80 text-sand rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
              </div>
            ))}
            {addon.images.length < 8 && (
              <button type="button" onClick={() => photoInput.current?.click()} data-testid="addon-photo-add-button" className="aspect-[4/3] rounded-xl border-2 border-dashed border-ink/15 grid place-items-center text-ink/40 hover:text-terra hover:border-terra/40 transition-colors"><ImagePlus size={20} /></button>
            )}
          </div>
          <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} data-testid="addon-photo-input" />
        </div>
      </div>
    </div>
  );
}
